// The gate matrix: every practice, its data source, and which features that turns on.
//
// David's ask — see who has what without opening each practice in turn. It sits under
// Mission Control because that is already the cross-practice supervision page, but it
// is fed by its own endpoint rather than the grid: the grid loads every claim for
// every practice, and it EXCLUDES a practice whose vendor does not resolve, which is
// exactly the row you most want on a matrix of who has what.
//
// Read-only. Changing a feature is done on the practice, in Practice Details, where
// the reason it is on or off is also visible.

import React, { useEffect, useState } from "react";
import "./CapabilityMatrix.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// Columns, in the order the work is done. Short headings because there are a dozen and
// more; the full name is in each cell's title attribute. CapabilityMatrix.test.js fails
// on any backend feature key (__fixtures__/feature-keys.json) without a column.
export const FEATURE_COLUMNS = [
  ["roster", "Roster", "Upcoming roster"],
  ["eligibility", "Elig", "Eligibility"],
  ["copay", "Copay", "Copay estimates"],
  ["patients", "Pts", "Patients"],
  ["authorizations", "Auths", "Authorizations"],
  ["charge_capture", "Charges", "Charge capture"],
  ["charge_templates", "Tmpl", "Charge templates"],
  ["auto_coder", "Coder", "Auto-coder (drafts yesterday's visits)"],
  ["cobalt_fee_schedules", "Fee pull", "Cobalt fee schedules (weekly pull)"],
  ["claim_editor", "Editor", "Claim editor"],
  ["fee_schedule", "Fees", "Fee schedule"],
  ["rules", "Rules", "Rules"],
  ["worklist", "Work", "Denial worklist"],
  ["writeoffs", "W/O", "Write-offs"],
  ["statements", "Stmts", "Patient statements"],
  ["collections", "Collect", "Collections"],
  ["submit_queue", "Queue", "Submit queue"],
  ["auth_advisor", "Advisor", "Auth advisor"],
  ["roster_send", "Send", "Send roster to practice"],
  ["exports", "CSV", "CSV exports"],
  ["reports", "Rpts", "Reports"],
  ["note_viewer", "Notes", "Note viewer"],
  ["coding_assist", "Assist", "Coding assist"],
  ["data_page", "Data", "Data page"],
  ["worklist_import", "Sheet", "Worklist import (biller sheet)"],
  ["card_charging", "Card", "Card charging (Atlas-controlled)"],
];

export default function CapabilityMatrix() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/practices/capabilities`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        if (!cancelled) setRows(data.practices || []);
      } catch (err) {
        if (!cancelled) setError("Could not load the feature matrix.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="cm-muted">{error}</p>;
  if (!rows) return <p className="cm-muted">Loading…</p>;
  if (!rows.length) return null;

  return (
    <div className="cm-section">
      <h2>Source &amp; features</h2>
      <p className="cm-muted">
        Which data source each practice has, and which features it turns on.
        Change one under Practice Details &rarr; Integrations &amp; features.
      </p>
      <div className="cm-scroll">
        <table className="cm-table">
          <thead>
            <tr>
              <th className="cm-practice-col">Practice</th>
              <th className="cm-source-col">Source</th>
              {FEATURE_COLUMNS.map(([key, short, full]) => (
                <th key={key} title={full}>
                  {short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="cm-practice-col">{row.name}</td>
                <td className="cm-source-col" title={row.source.reason}>
                  {row.source.key ? (
                    row.source.label
                  ) : (
                    <span className="cm-none">none</span>
                  )}
                </td>
                {FEATURE_COLUMNS.map(([key, , full]) => {
                  const on = row.features[key];
                  const forced = key in (row.overrides || {});
                  return (
                    <td
                      key={key}
                      className={`cm-cell${on ? " on" : ""}${forced ? " forced" : ""}`}
                      title={
                        `${full}: ${on ? "on" : "off"}` +
                        (forced ? " (forced by an override)" : "")
                      }
                    >
                      {on ? "●" : "·"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="cm-legend">
        <span className="cm-cell on">●</span> on
        <span className="cm-cell">·</span> off
        <span className="cm-cell on forced">●</span> forced by an override
      </p>
    </div>
  );
}
